import { t } from '@trimble/i18n';
import { Component, JSX, Show, mergeProps, onCleanup, onMount } from 'solid-js';

export interface ConfirmModusHandle {
  show(): void;
  close(): void;
  getDialog(): HTMLDialogElement | null;
}

interface IConfirmModus {
  id?: string;
  header?: string;
  description?: string;
  children?: JSX.Element;
  showClose?: boolean;
  primaryButtonLabel?: string;
  primaryButtonClass?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'warning'
    | 'danger';
  /** Omit or pass null/empty to hide the secondary footer button. */
  secondaryButtonLabel?: string | null;
  secondaryButtonClass?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'warning'
    | 'danger';
  onCancel(): void;
  onConfirm(): void;
  modalRef(handle: ConfirmModusHandle): void;
}

export const ConfirmModus: Component<IConfirmModus> = (p: IConfirmModus) => {
  const props = mergeProps(
    {
      id: 'confirm-modus-modal',
      primaryButtonLabel: t('general.yes'),
      secondaryButtonLabel: t('general.no'),
      showClose: true,
    },
    p
  );

  let modalEl: HTMLDialogElement | null = null;
  let initialized = false;
  let closeReason: 'confirm' | 'cancel' | 'dismiss' = 'dismiss';

  const handleDialogClose = (): void => {
    const reason = closeReason;
    closeReason = 'dismiss';
    // The Modus close control dismisses the native dialog without using a
    // footer button. Treat that as cancel; footer actions set closeReason first.
    if (reason === 'dismiss') {
      props.onCancel();
    }
  };

  //this must run after the component is hydrated
  const getDialog = (): HTMLDialogElement | null => {
    if (!initialized) {
      const byModalId = document.getElementById(
        props.id
      ) as HTMLDialogElement | null;
      if (byModalId && typeof byModalId.close === 'function') {
        modalEl = byModalId;
      } else {
        const modalWrapper = document.getElementById(`${props.id}-dialog`);
        modalEl =
          modalWrapper?.querySelector('dialog') ??
          modalWrapper?.shadowRoot?.querySelector('dialog') ??
          null;
      }
      initialized = modalEl !== null;
      modalEl?.addEventListener('close', handleDialogClose);
    }
    return modalEl;
  };

  onCleanup(() => {
    modalEl?.removeEventListener('close', handleDialogClose);
  });

  onMount(() => {
    props.modalRef({
      show: () => {
        if (!initialized) getDialog();
        modalEl?.showModal?.();
      },
      close: () => {
        if (!initialized) getDialog();
        modalEl?.close?.();
      },
      getDialog,
    });
    getDialog();
  });

  return (
    <>
      <modus-wc-modal
        aria-label="confirm-modal"
        custom-class=""
        fullscreen={false}
        id={props.id ? `${props.id}-dialog` : 'confirm-modus-modal'}
        modal-id={props.id ?? 'confirm-modus-modal'}
        backdrop="default"
        position="center"
        show-close={props.showClose}
        show-fullscreen-toggle="false"
      >
        <span slot="header">{props.header}</span>
        <div slot="content">{props.children ?? props.description}</div>
        <Show when={props.secondaryButtonLabel}>
          {(label) => (
            <modus-wc-button
              slot="footer"
              id="close-modal-btn-secondary"
              size="md"
              variant="filled"
              color={props.secondaryButtonClass ?? 'secondary'}
              onClick={() => {
                closeReason = 'cancel';
                props.onCancel();
                getDialog()?.close();
              }}
            >
              {label()}
            </modus-wc-button>
          )}
        </Show>
        <modus-wc-button
          slot="footer"
          id="close-modal-btn-primary"
          size="md"
          variant="filled"
          color={props.primaryButtonClass ?? 'primary'}
          onClick={() => {
            closeReason = 'confirm';
            props.onConfirm();
            getDialog()?.close();
          }}
        >
          {props.primaryButtonLabel}
        </modus-wc-button>
      </modus-wc-modal>
    </>
  );
};
